import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type * as Microcredits from '@src/@types/microcredits';
import { DEFAULT_FILTERS, FILTERS_NAMES, MODES } from '@src/constants/filters';
import { ListKeys } from '@src/constants/microcredits';
import { useCurrentLocation } from '@src/reducers/locations';
import { useSelectCreditsFilters } from '@src/reducers/microcredits';

import { useLoadClientOffers } from './useLoadClientOffers';
import { useQueryReplace } from './useQueryReplace';

const DIFFERENCE_SET = new Set([
  FILTERS_NAMES.SECURITY,
  FILTERS_NAMES.GUARANTEE,
  FILTERS_NAMES.OBTAINING_METHOD,
  FILTERS_NAMES.CURRENCY,
  FILTERS_NAMES.PROLONGATION,
  FILTERS_NAMES.POSTPONEMENT,
  FILTERS_NAMES.BANKS,
]);

const findDifferenceCount = (filters, defaultsFilters) =>
  Object.values(FILTERS_NAMES).reduce(
    (count, key) => (DIFFERENCE_SET.has(key) && !isEqual(filters[key], defaultsFilters[key]) ? count + 1 : count),
    0,
  );

interface IParams {
  name?: ListKeys;
  defaultValue?: Partial<Microcredits.IFilters>;
}

export type IFilter = ReturnType<typeof useFilter>;

export const useFilter = (params: IParams = {}) => {
  const { name = ListKeys.LIST, defaultValue = DEFAULT_FILTERS[MODES.DEFAULT] } = params || {};

  const currentLocation = useCurrentLocation();
  const filters = useSelectCreditsFilters(name);
  const [filtersState, setFiltersState] = useState(omit(filters, ['metadata']));

  useEffect(() => {
    setFiltersState(omit(filters, ['metadata']));
  }, [filters]);

  const difference = useMemo(() => findDifferenceCount(filtersState, defaultValue), [defaultValue, filtersState]);
  const defaultFilters = useMemo(
    () => ({ ...defaultValue, [FILTERS_NAMES.LOCATION]: currentLocation.route }),
    [currentLocation.route, defaultValue],
  );
  const updateQuery = useQueryReplace();

  const loadClientOffers = useLoadClientOffers(name);

  const updateFilterMap = useCallback(
    (updateMap: Partial<Microcredits.IFilters>, isPersistOffers?: boolean) => {
      setFiltersState((original) => {
        if (updateMap) {
          const newFilters = Object.assign(original, updateMap);

          loadClientOffers(newFilters, isPersistOffers);

          return newFilters;
        }

        return original;
      });
    },
    [loadClientOffers],
  );

  const setFilter = useCallback(
    (key: string, value: any, isPersistOffers?: boolean) => {
      setFiltersState((original) => {
        if (original[key] !== value) {
          const newFilters = { ...original, [key]: value };

          loadClientOffers(newFilters, isPersistOffers);

          return newFilters;
        }

        return original;
      });
    },
    [loadClientOffers],
  );

  const resetFilters = (): void => {
    if (!isEqual(defaultFilters, filtersState)) {
      setFiltersState(defaultFilters);

      updateQuery({
        newQuery: defaultFilters,
        defaultQuery: defaultFilters,
      });
    }
  };

  return {
    filters: filtersState,
    setFilter,
    updateFilterMap,
    difference,
    resetFilters,
  };
};
